// reducers/signUpSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { showSuccess } from "../../helpers/toast";
import { saveMfaDeviceToken } from "../../helpers/mfaDeviceToken";
import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

const SEND_OTP_PATH =
  process.env.REACT_APP_SIGNUP_SEND_OTP_PATH || "/users/signup/send-otp";

const resolveCodeExpiresAt = (data) => {
  if (data?.code_expires_at || data?.codeExpiresAt) {
    return data.code_expires_at || data.codeExpiresAt;
  }
  const seconds = Number(data?.code_expires_in ?? data?.codeExpiresIn);
  if (Number.isFinite(seconds) && seconds > 0) {
    return new Date(Date.now() + seconds * 1000).toISOString();
  }
  return new Date(Date.now() + 600 * 1000).toISOString();
};

const apiErrorPayload = (error, fallback) => {
  const data = error?.response?.data;
  const message =
    (typeof data?.error === "string" && data.error) ||
    data?.message ||
    data?.reason ||
    error?.message ||
    fallback;
  const status = error?.response?.status;
  const lower = String(message || "").toLowerCase();
  const otpExpired =
    /verification code expired|code expired|otp expired|expired code|request a new code|otp has expired|code has expired/.test(
      lower
    );
  const invalidOtp =
    !otpExpired &&
    (/invalid verification code|incorrect (verification )?code|wrong (otp|code)|invalid (otp|code)|does not match|mismatch/.test(
      lower
    ) ||
      ((status === 400 || status === 401 || status === 422) &&
        /(otp|verification code|one[- ]time)/.test(lower) &&
        !/password/.test(lower)));
  return {
    message,
    status,
    invalidSignupCode: /invalid signup code/.test(lower),
    invalidPhone: /valid mobile number|can receive sms/.test(lower),
    rateLimited: status === 429,
    otpRequired: /verification code is required/.test(lower),
    invalidOtp,
    otpExpired,
    unavailable: status === 503 || /phone verification is unavailable/.test(lower),
    // Never treat OTP failures as password errors (those redirect away)
    passwordError:
      /password/.test(lower) &&
      status === 400 &&
      !otpExpired &&
      !invalidOtp &&
      !/verification code|otp/.test(lower),
  };
};

const persistSignupDeviceToken = (data) => {
  const token = data?.mfa_device_token;
  if (!token) return;
  saveMfaDeviceToken(
    data?.user?.email || null,
    token,
    data?.mfa_device_expires_at || data?.mfa_device_expiresAt || null
  );
};

const initialState = {
  data: null,
  signupcode: null,
  password: null,
  full_name: null,
  phone: null,
  companyRegistrationNo: null,
  otpRequired: false,
  phoneHint: null,
  codeExpiresAt: null,
  codeExpiresIn: null,
  resendBlocked: false,
  loading: false,
  message: null,
  error: null,
  isSuccess: false,
};

/** POST /users/signup/send-otp — texts code or returns otp_required: false */
export const sendSignupOtp = createAsyncThunk(
  "signUp/sendOtp",
  async ({ signupcode, phone }, { rejectWithValue }) => {
    try {
      const response = await API.post(SEND_OTP_PATH, {
        signupcode,
        phone,
      });
      const data = response.data || {};
      if (data.otp_required === false || data.otpRequired === false) {
        return { otpRequired: false };
      }
      return {
        otpRequired: true,
        phoneHint: data.phone_hint || data.phoneHint || null,
        codeExpiresAt: resolveCodeExpiresAt(data),
        codeExpiresIn: data.code_expires_in ?? data.codeExpiresIn ?? 600,
        otpChannel: data.otp_channel || data.otpChannel || "sms",
      };
    } catch (error) {
      return rejectWithValue(
        apiErrorPayload(error, "Failed to send verification code.")
      );
    }
  }
);

export const signUp = createAsyncThunk(
  "signUp/",
  async (data, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
        },
      };
      const body = { ...data.userData };
      if (body.otp != null) {
        body.otp = String(body.otp).trim();
      }
      const response = await API.post("/users/signup", body, config);
      persistSignupDeviceToken(response.data);
      showSuccess("Registered successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        apiErrorPayload(error, "Failed to register user.")
      );
    }
  }
);

const signUpSlice = createSlice({
  name: "signUp",
  initialState,
  reducers: {
    updateSignUpData: (state, action) => {
      const p = action.payload || {};
      if (p.signupcode !== undefined) state.signupcode = p.signupcode;
      if (p.password !== undefined) state.password = p.password;
      if (p.full_name !== undefined) state.full_name = p.full_name;
      if (p.phone !== undefined) state.phone = p.phone;
      if (p.companyRegistrationNo !== undefined) {
        state.companyRegistrationNo = p.companyRegistrationNo;
      }
    },
    clearSignupOtpChallenge: (state) => {
      state.otpRequired = false;
      state.phoneHint = null;
      state.codeExpiresAt = null;
      state.codeExpiresIn = null;
      state.resendBlocked = false;
    },
    clearSignupData: (state) => {
      state.signupcode = null;
      state.password = null;
      state.full_name = null;
      state.phone = null;
      state.companyRegistrationNo = null;
      state.otpRequired = false;
      state.phoneHint = null;
      state.codeExpiresAt = null;
      state.codeExpiresIn = null;
      state.resendBlocked = false;
      state.isSuccess = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendSignupOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendSignupOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.otpRequired = !!action.payload?.otpRequired;
        state.phoneHint = action.payload?.phoneHint || null;
        state.codeExpiresAt = action.payload?.codeExpiresAt || null;
        state.codeExpiresIn = action.payload?.codeExpiresIn || null;
        state.resendBlocked = false;
        state.error = null;
      })
      .addCase(sendSignupOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
        if (action.payload?.rateLimited) {
          state.resendBlocked = true;
        }
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action;
        state.message = action.payload;
        state.error = null;
        state.isSuccess = true;
        state.otpRequired = false;
        state.phoneHint = null;
        state.codeExpiresAt = null;
        state.codeExpiresIn = null;
        state.resendBlocked = false;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.loading = false;
        state.isSuccess = false;
        state.data = action;
        state.error = action.payload || action.error;
      });
  },
});

export const { updateSignUpData, clearSignupOtpChallenge, clearSignupData } =
  signUpSlice.actions
export default signUpSlice.reducer;
